import type { FleetMetricAggregate } from '@immo/api-contract';

/**
 * The four token classes, ordered by UNIT PRICE — cheapest first (IMM-911
 * design run). The order is ordinal, which is why one neutral ramp encodes
 * it, light → dark = cheap → expensive, and the semantic tones stay free for
 * the honesty states.
 */
export const TOKEN_CLASSES = ['cacheRead', 'uncachedInput', 'cacheWrite', 'output'] as const;

export type TokenClass = (typeof TOKEN_CLASSES)[number];

export type ClassValues = Readonly<Record<TokenClass, number>>;

/** The agent vocabulary stays English in every locale. */
export const TOKEN_CLASS_LABEL: Readonly<Record<TokenClass, string>> = {
  cacheRead: 'cache read',
  uncachedInput: 'uncached input',
  cacheWrite: 'cache write',
  output: 'output',
};

type ModelPrices = ClassValues;

/**
 * Money is configuration; tokens are the measurement. No record carries an
 * amount — euro is computed on read from this dated table, and older runs are
 * valued at today's prices on purpose, because an amount frozen on a record
 * turns wrong the day a price changes.
 *
 * USD per million tokens, keyed by the ledger's `model` field. A model the
 * table does not name is valued at `fallbackModel`'s prices, and the page
 * footnote names every such model.
 */
export const FLEET_PRICING = {
  asOf: '2026-09-01',
  eurPerUsd: 0.92,
  fallbackModel: 'opus',
  usdPerMillion: {
    opus: { cacheRead: 1.5, uncachedInput: 15, cacheWrite: 18.75, output: 75 },
    sonnet: { cacheRead: 0.3, uncachedInput: 3, cacheWrite: 3.75, output: 15 },
  },
} as const satisfies {
  readonly asOf: string;
  readonly eurPerUsd: number;
  readonly fallbackModel: string;
  readonly usdPerMillion: Readonly<Record<string, ModelPrices>>;
};

type PricedModel = keyof typeof FLEET_PRICING.usdPerMillion;

export function isPriced(model: string): boolean {
  return Object.hasOwn(FLEET_PRICING.usdPerMillion, model);
}

export function sumClasses(values: ClassValues): number {
  return TOKEN_CLASSES.reduce((sum, key) => sum + values[key], 0);
}

/** The four classes as the series aggregates carry them. */
export function tokensByClass(aggregate: FleetMetricAggregate): ClassValues {
  return {
    cacheRead: aggregate.cacheReadInputTokens,
    uncachedInput: aggregate.uncachedInputTokens,
    cacheWrite: aggregate.cacheWriteInputTokens,
    output: aggregate.outputTokens,
  };
}

/** Euro for one model's tokens. */
export function costOf(model: string, tokens: ClassValues): number {
  const prices = FLEET_PRICING.usdPerMillion[(isPriced(model) ? model : FLEET_PRICING.fallbackModel) as PricedModel];

  return TOKEN_CLASSES.reduce((sum, key) => sum + (tokens[key] / 1_000_000) * prices[key] * FLEET_PRICING.eurPerUsd, 0);
}
